/**
 * product-media.js
 * Gallery controller for snippets/product-media.liquid
 * Task: K-056 (original) + PDP gallery overhaul (2026-05-19)
 *
 * Responsibilities:
 *  1. Hover-zoom (desktop @media hover:hover) — unified side-panel lens to the
 *     right of the HERO. Any element marked `[data-zoom-source]` (hero or any
 *     thumb) acts as a zoom trigger; on hover the shared panel populates with
 *     that element's full-size image and tracks the cursor.
 *  2. Mobile tap-to-zoom — opens K-024 modal with full-res image. The
 *     bottom-right zoom button is the visible affordance for the same action
 *     and zooms the slide currently in view (index from scroll position).
 *  3. Variant-change → filters thumb visibility (hide thumbs that belong to
 *     other variants).
 *  4. Mobile horizontal carousel — scroll-progress bar tracks scroll position.
 *
 * Thumbs are NOT click-to-swap — each image stays in place. Removed in the
 * 2026-05-19 overhaul per merchant request.
 *
 * No framework. No npm packages.
 */

(function () {
  'use strict';

  /**
   * Returns true when the device supports hover (i.e. has a mouse/trackpad).
   * Checked once on init so we don't query media on every mouse-move.
   * @returns {boolean}
   */
  function supportsHover() {
    return window.matchMedia('(hover: hover) and (pointer: fine)').matches;
  }

  /**
   * Initialises a single `.product-media` component.
   * @param {HTMLElement} root
   */
  function init(root) {
    var primaryWrap = root.querySelector('[data-product-media-primary]');
    var primaryImg = primaryWrap ? primaryWrap.querySelector('.product-media__primary-img') : null;
    var zoomPanel = root.querySelector('[data-zoom-panel]');
    var thumbsWrap = root.querySelector('.product-media__thumbs');
    var thumbEls = thumbsWrap ? Array.from(thumbsWrap.querySelectorAll('.product-media__thumb')) : [];

    var zoomModal = document.getElementById('product-media-zoom');
    var zoomViewerImg = zoomModal ? zoomModal.querySelector('[data-zoom-viewer-img]') : null;

    var hasHover = supportsHover();

    /* ── Unified hover-zoom (desktop only) ──────────────────────────────────
       The zoom panel is a child of the HERO so it positions next to the hero
       regardless of which element triggered the zoom. Any `[data-zoom-source]`
       element (hero or thumb) becomes a trigger:
         - mouseenter: populate panel bg with that element's image
         - mousemove:  update background-position from cursor coords
         - mouseleave: hide panel
       Hero image NEVER transforms — the panel renders the zoom. */

    if (zoomPanel && hasHover) {
      var zoomSources = Array.from(root.querySelectorAll('[data-zoom-source]'));

      /**
       * Returns the image URL to use as the zoom background for `el`.
       * Priority: data-full-src → inner <img> currentSrc/src.
       * @param {HTMLElement} el
       * @returns {string}
       */
      function zoomSrcFor(el) {
        if (el.dataset && el.dataset.fullSrc) return el.dataset.fullSrc;
        var img = el.querySelector('img');
        if (img) return img.currentSrc || img.src || '';
        return '';
      }

      var activeZoomSource = null;

      /**
       * Measures the bottom edge of whatever site header is currently
       * occupying the top of the viewport. Detection is geometry-based, not
       * CSS-property-based, because the sticky behavior may live on a
       * parent wrapper (e.g. `shopify-section-group-header`) rather than
       * the visible header element itself. If the header's bounding rect
       * sits at the top of the viewport with positive height, treat it as
       * occupying that space regardless of `position` value.
       */
      function stickyHeaderBottom() {
        var header = document.querySelector('.site-header, header.header, .header');
        if (!header) return 0;
        var r = header.getBoundingClientRect();
        if (r.top <= 0 && r.bottom > 0) return r.bottom;
        return 0;
      }

      function positionZoomFor(el) {
        /* Place the panel beside the hovered source. Uses fixed positioning
           so it sits in viewport coords. Top floor is the sticky header's
           bottom edge (so the panel never hides behind the nav); bottom is
           viewport - 16. Height capped to fit between them. */
        var rect = el.getBoundingClientRect();
        var vh = window.innerHeight;
        var topMargin = stickyHeaderBottom() + 16;
        var bottomMargin = 16;
        var maxPanelH = vh - topMargin - bottomMargin;
        var panelH = Math.min(rect.height, maxPanelH);
        var top = rect.top;
        if (top + panelH > vh - bottomMargin) {
          top = vh - bottomMargin - panelH;
        }
        if (top < topMargin) top = topMargin;
        zoomPanel.style.top = top + 'px';
        zoomPanel.style.left = rect.right + 16 + 'px';
        zoomPanel.style.height = panelH + 'px';
      }

      /* Scroll re-position — runs while a zoom is active so the panel follows
         the moving thumb as the page scrolls under a stationary cursor. rAF-
         throttled to one update per frame so heavy scrolling doesn't reflow
         the panel position 100+ times per second. */
      var rafPending = false;
      function onScroll() {
        if (!activeZoomSource || rafPending) return;
        rafPending = true;
        requestAnimationFrame(function () {
          rafPending = false;
          if (activeZoomSource) positionZoomFor(activeZoomSource);
        });
      }
      window.addEventListener('scroll', onScroll, { passive: true });

      function showZoom(el) {
        var src = zoomSrcFor(el);
        if (!src) return;
        activeZoomSource = el;
        positionZoomFor(el);
        zoomPanel.style.backgroundImage = 'url("' + src + '")';
        zoomPanel.classList.add('is-visible');
      }

      function moveZoom(el, e) {
        var rect = el.getBoundingClientRect();
        if (rect.width === 0 || rect.height === 0) return;
        var xPct = Math.min(Math.max((e.clientX - rect.left) / rect.width, 0), 1);
        var yPct = Math.min(Math.max((e.clientY - rect.top) / rect.height, 0), 1);
        zoomPanel.style.backgroundPosition = (xPct * 100) + '% ' + (yPct * 100) + '%';
      }

      function hideZoom() {
        activeZoomSource = null;
        zoomPanel.classList.remove('is-visible');
      }

      zoomSources.forEach(function (el) {
        el.addEventListener('mouseenter', function () { showZoom(el); });
        el.addEventListener('mousemove', function (e) { moveZoom(el, e); });
        el.addEventListener('mouseleave', hideZoom);
      });
    }

    /* ── Variant change → filter thumb visibility ──────────────────────────── */

    /* Walk up to the parent product section so we can listen for variant:change. */
    var section = root.closest('[id^="product-"]');
    if (section && thumbEls.length > 0) {
      section.addEventListener('variant:change', function (e) {
        var variant = e.detail && e.detail.variant;
        if (!variant) return;

        var newId = String(variant.id);
        root.dataset.currentVariantId = newId;

        var visibleCount = 0;

        thumbEls.forEach(function (el) {
          var owners = (el.dataset.ownerVariants || '').split(',').filter(Boolean);
          var isShared = owners.length === 0;
          var isOwnedByCurrent = owners.indexOf(newId) !== -1;
          var visible = isShared || isOwnedByCurrent;

          if (visible) {
            el.hidden = false;
            el.removeAttribute('aria-hidden');
            el.classList.remove('is-hidden');
            visibleCount++;
          } else {
            el.hidden = true;
            el.setAttribute('aria-hidden', 'true');
            el.classList.add('is-hidden');
          }
        });

        /* Hide the thumbs container entirely when 0 visible — keep it when
           exactly 1 visible (still a useful zoom target). */
        if (thumbsWrap) {
          thumbsWrap.hidden = visibleCount < 1;
        }
      });
    }

    /* ── Mobile horizontal carousel — scroll-progress bar ────────────────────
       Mirrors the [data-ctl-progress] controller in sections/product.liquid:
       a fixed-width fill that translates across its track in proportion to how
       far the strip has been scrolled.

       The fill's WIDTH is set in CSS from --mobile-slide-count (100/N percent),
       so it is already correct before this runs and stays correct without JS.
       All this does is translate it.

       Travel is computed in PIXELS, not as a percentage. A percentage in
       translateX() resolves against the ELEMENT's own width, not the track's,
       so `translateX(ratio * (1 - thumbWidth) * 100%)` — the form used by the
       CTL bar — under-travels by a factor of thumbWidth and never reaches the
       right edge. Pixels avoid that trap entirely. Flagged for the CTL bar. */
    var mobileTrack = root.querySelector('[data-mobile-track]');
    var mobileProgress = root.querySelector('[data-mobile-progress]');

    if (mobileTrack && mobileProgress) {
      var progressRaf = 0;
      var updateMobileProgress = function () {
        progressRaf = 0;
        var max = mobileTrack.scrollWidth - mobileTrack.clientWidth;
        if (max <= 0) return;
        var ratio = mobileTrack.scrollLeft / max;
        if (ratio < 0) ratio = 0;
        if (ratio > 1) ratio = 1;
        var travel = mobileTrack.clientWidth - mobileProgress.offsetWidth;
        if (travel < 0) travel = 0;
        mobileProgress.style.transform = 'translateX(' + (ratio * travel) + 'px)';
      };
      var scheduleMobileProgress = function () {
        if (progressRaf) return;
        progressRaf = window.requestAnimationFrame(updateMobileProgress);
      };
      mobileTrack.addEventListener('scroll', scheduleMobileProgress, { passive: true });
      window.addEventListener('resize', scheduleMobileProgress);
      updateMobileProgress();
    }

    /* ── Click/tap to open the full-screen zoom ────────────────────────────
       Was mobile-only: the hero's handler bailed out via `if (hasHover)
       return`, so on a laptop clicking an image did nothing even though the
       CSS advertises `cursor: zoom-in`. Desktop now opens the same modal the
       phone does. The hover lens stays as it was — it is a different
       affordance, not a replacement for opening the image. */

    var zoomViewer = zoomModal ? zoomModal.querySelector('[data-zoom-viewer]') : null;
    var zoomPrev = zoomModal ? zoomModal.querySelector('[data-zoom-prev]') : null;
    var zoomNext = zoomModal ? zoomModal.querySelector('[data-zoom-next]') : null;
    var zoomCounter = zoomModal ? zoomModal.querySelector('[data-zoom-counter]') : null;
    var zoomAnnounce = zoomModal ? zoomModal.querySelector('[data-zoom-announce]') : null;
    var zoomIndex = 0;

    /* Recomputed on every open: thumbs are shown and hidden per variant, so a
       list captured once would offer images the shopper cannot currently see. */
    function visibleSources() {
      return Array.from(root.querySelectorAll('[data-zoom-source]')).filter(function (el) {
        return !el.hidden && !el.classList.contains('is-hidden');
      });
    }

    function srcOf(el) {
      var img = el.querySelector('img');
      return el.dataset.fullSrc || (img ? img.currentSrc || img.src : '');
    }

    function renderZoom(list, index) {
      var el = list[index];
      if (!el || !zoomViewerImg) return;
      var img = el.querySelector('img');
      zoomViewerImg.src = srcOf(el);
      zoomViewerImg.alt = img ? img.alt : '';

      /* Replay the entry animation so stepping between images reads as a
         change rather than a silent swap. */
      zoomViewerImg.style.animation = 'none';
      void zoomViewerImg.offsetWidth;
      zoomViewerImg.style.animation = '';

      var many = list.length > 1;
      if (zoomPrev) zoomPrev.hidden = !many;
      if (zoomNext) zoomNext.hidden = !many;
      if (zoomCounter) {
        zoomCounter.hidden = !many;
        zoomCounter.textContent = index + 1 + ' / ' + list.length;
      }
      if (zoomAnnounce && zoomViewer) {
        zoomAnnounce.textContent = (zoomViewer.dataset.counterTemplate || '')
          .replace('[current]', index + 1)
          .replace('[total]', list.length);
      }

      /* Warm the neighbours so arrowing through does not flash. */
      [index - 1, index + 1].forEach(function (n) {
        var neighbour = list[(n + list.length) % list.length];
        if (neighbour) {
          var pre = new Image();
          pre.src = srcOf(neighbour);
        }
      });
    }

    function stepZoom(delta) {
      var list = visibleSources();
      if (list.length < 2) return;
      zoomIndex = (zoomIndex + delta + list.length) % list.length;
      renderZoom(list, zoomIndex);
    }

    function openZoomAt(index) {
      var list = visibleSources();
      if (!zoomModal || list.length === 0) return;
      zoomIndex = Math.max(0, Math.min(index, list.length - 1));
      /* The hover lens is a sibling layer; leaving it on parks a magnified
         strip underneath the dialog. */
      if (zoomPanel) zoomPanel.classList.remove('is-visible');
      renderZoom(list, zoomIndex);
      if (typeof zoomModal.showModal === 'function') {
        zoomModal.showModal();
        zoomModal.dispatchEvent(new CustomEvent('dialog:open', { bubbles: true }));
      }
    }

    /* Mobile slides pass their <img>; match it back to a gallery source by
       file path so the carousel and the lightbox stay on the same image. */
    function openZoomFromImg(img) {
      if (!img) return;
      var list = visibleSources();
      var target = (img.dataset.fullSrc || img.currentSrc || img.src || '').split('?')[0];
      var found = -1;
      list.forEach(function (el, i) {
        if (found === -1 && (srcOf(el) || '').split('?')[0] === target) found = i;
      });
      openZoomAt(found >= 0 ? found : 0);
    }

    if (zoomPrev) zoomPrev.addEventListener('click', function () { stepZoom(-1); });
    if (zoomNext) zoomNext.addEventListener('click', function () { stepZoom(1); });

    if (zoomModal) {
      zoomModal.addEventListener('keydown', function (event) {
        if (event.key === 'ArrowLeft') { event.preventDefault(); stepZoom(-1); }
        else if (event.key === 'ArrowRight') { event.preventDefault(); stepZoom(1); }
      });
    }

    function openZoomFromSource(el) {
      var list = visibleSources();
      var index = -1;
      list.forEach(function (candidate, i) { if (candidate === el) index = i; });
      openZoomAt(index >= 0 ? index : 0);
    }

    if (primaryWrap && zoomModal) {
      primaryWrap.addEventListener('click', function () {
        openZoomFromSource(primaryWrap);
      });
    }

    /* Thumbs open the image they show. Delegated, because thumbs are shown and
       hidden per variant. */
    if (zoomModal) {
      root.addEventListener('click', function (event) {
        if (!event.target.closest) return;
        var thumb = event.target.closest('.product-media__thumb[data-zoom-source]');
        if (thumb) openZoomFromSource(thumb);
      });

      /* The wrappers are divs, so they get keyboard activation explicitly —
         Enter and Space, matching what a real button would do. */
      root.addEventListener('keydown', function (event) {
        if (event.key !== 'Enter' && event.key !== ' ' && event.key !== 'Spacebar') return;
        if (!event.target.closest) return;
        var source = event.target.closest('[data-zoom-source]');
        if (!source) return;
        event.preventDefault();
        openZoomFromSource(source);
      });
    }

    /* Mobile carousel images — tap to open zoom modal. */
    if (mobileTrack && zoomModal) {
      mobileTrack.addEventListener('click', function (event) {
        var img = event.target.closest && event.target.closest('.product-media__mobile-img');
        if (!img) return;
        openZoomFromImg(img);
      });
    }

    /* ── Mobile zoom button (visible affordance for the same tap-to-zoom) ────
       The strip is a scroll-snap carousel, so the button must zoom whatever
       slide is IN VIEW — always zooming media[0] looks correct on the first
       slide and is wrong from the second onward.

       The active index is derived from the SAME scroll ratio the progress bar
       uses (scrollLeft / (scrollWidth - clientWidth)) rather than a second
       measurement: with N equal, full-width slides that ratio is exactly
       k / (N - 1) at slide k, so rounding recovers k. A non-scrollable track
       (one slide, or images not laid out yet) yields max <= 0 and falls back
       to index 0. */
    var mobileZoomBtn = root.querySelector('[data-mobile-zoom]');

    if (mobileZoomBtn && mobileTrack && zoomModal) {
      mobileZoomBtn.addEventListener('click', function () {
        var slides = mobileTrack.querySelectorAll('.product-media__mobile-slide');
        if (slides.length === 0) return;

        var index = 0;
        var max = mobileTrack.scrollWidth - mobileTrack.clientWidth;
        if (slides.length > 1 && max > 0) {
          var ratio = mobileTrack.scrollLeft / max;
          if (ratio < 0) ratio = 0;
          if (ratio > 1) ratio = 1;
          index = Math.round(ratio * (slides.length - 1));
        }

        openZoomFromImg(slides[index].querySelector('.product-media__mobile-img'));
      });
    } else if (mobileZoomBtn) {
      /* No track or no modal to open — hide the affordance rather than leave a
         button that does nothing when pressed. */
      mobileZoomBtn.hidden = true;
    }
  }

  /* ── Boot ─────────────────────────────────────────────────────────────────── */

  document.querySelectorAll('[data-product-media]').forEach(init);
})();
